/** Shared by browser and server. No raw request, wallet or vendor Error is persisted. */
export function cleanWalletErrorMessage(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  let text = value.slice(0, 2048)
    // eslint-disable-next-line no-control-regex -- Remove terminal escape sequences from untrusted vendor text.
    .replace(/\u001b\[[0-9;]*m/g, "")
    .replace(/\[TON_CONNECT_(?:SDK|UI)_ERROR\]\s*/g, "")
    // eslint-disable-next-line no-control-regex -- Diagnostic text must not contain control characters.
    .replace(/[\u0000-\u001f\u007f]/g, " ");
  // Requests, proofs and credentials can be embedded in an otherwise useful error.
  // Keep the explanation before them; never attempt to serialize nested data.
  text = text.split(/\{|\[\{|\b(?:payload|state_?init|walletStateInit|boc|proof|signature|authorization|bearer|initData|mnemonic|seed|private.?key|public.?key|password|secret|api.?key|access.?token|refresh.?token)\b\s*[:=]?/i)[0] ?? "";
  text = text
    .replace(/(?:https?|wss?|ton|tc|tg|tonkeeper):\/\/\S+/gi, "[url]")
    .replace(/\b\d{5,}:[a-zA-Z0-9_-]{20,}/g, "[credential]")
    .replace(/(?:-?\d{1,3}:)?\b[a-f0-9]{64}\b/gi, "[address]")
    .replace(/\b(?:EQ|UQ|kQ|0Q)[a-zA-Z0-9_+/-]{46}\b/g, "[address]")
    .replace(/\b[^\s@]+@[^\s@]+\.[^\s@]+\b/g, "[email]")
    .replace(/[a-zA-Z0-9_+/=-]{40,}/g, "[data]")
    .replace(/\b\d{9,}\b/g, "[number]")
    .replace(/\s+/g, " ").trim();
  return text ? text.slice(0, 240) : undefined;
}

export function walletProtocolErrorCode(value: unknown): number | undefined {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0 && value <= 65535 ? value : undefined;
}
export function walletTraceId(value: unknown): string | undefined {
  return typeof value === "string" && /^[a-f0-9]{8}-(?:[a-f0-9]{4}-){3}[a-f0-9]{12}$/i.test(value) ? value : undefined;
}
export type WalletErrorCode = "wallet_rejected" | "wallet_request_expired" | "wallet_disconnected" | "wallet_network_error" | "wallet_unsupported" | "wallet_invalid_request" | "wallet_insufficient_balance" | "wallet_unknown_error";
export type WalletErrorDetails = {
  code: WalletErrorCode; errorCode?: number | undefined; errorMessage?: string | undefined;
  errorName?: string | undefined; errorSource: "wallet" | "sdk" | "open";
};
export function classifyWalletError(message: string, protocolCode?: number): WalletErrorCode {
  if (protocolCode === 300) return "wallet_rejected";
  if (protocolCode === 100) return "wallet_disconnected";
  if (protocolCode === 400) return "wallet_unsupported";
  if (protocolCode === 1) return "wallet_invalid_request";
  if (/expired|timed out|timeout/i.test(message)) return "wallet_request_expired";
  if (/insufficient.*(?:balance|funds)|not enough.*(?:balance|funds|TON|GRAM)/i.test(message)) return "wallet_insufficient_balance";
  // With no protocol response, known SDK classes still provide useful context.
  if (protocolCode === undefined && /UserReject|user.*reject|cancelled|canceled/i.test(message)) return "wallet_rejected";
  if (/UnknownAppError|unknown.?app|not connected|connect_wallet|account_changed|wallet_unverified|WalletNotConnected/i.test(message)) return "wallet_disconnected";
  if (/not.?support|unsupported/i.test(message)) return "wallet_unsupported";
  if (/bad.?request|invalid|wrong.*network/i.test(message)) return "wallet_invalid_request";
  if (/network|Failed to fetch|bridge|connection|fetch.*fail/i.test(message)) return "wallet_network_error";
  return "wallet_unknown_error";
}
export function describeWalletError(error: unknown, source: "sdk" | "open" = "sdk"): WalletErrorDetails {
  if (error instanceof MarketWalletRequestError) return error.details;
  const message = error instanceof Error ? `${error.constructor.name} ${error.message}` : "";
  const errorCode = walletProtocolErrorCode((error as { code?: unknown } | null)?.code);
  const name = error instanceof Error ? error.constructor.name : undefined;
  return { code: classifyWalletError(message, errorCode), errorCode, errorMessage: cleanWalletErrorMessage(message),
    errorName: name && /^[a-zA-Z][a-zA-Z0-9_]{0,63}$/.test(name) ? name : undefined, errorSource: source };
}
/** A controlled wrapper carries protocol details through the SDK's code-less Error. */
export class MarketWalletRequestError extends Error {
  readonly details: WalletErrorDetails;
  constructor(details: WalletErrorDetails, cause: unknown) {
    super(details.code); this.details = details; this.name = "MarketWalletRequestError";
    // The shared backend type library predates ErrorOptions. Keep the original
    // error non-enumerable without requiring a global compiler-target change.
    Object.defineProperty(this, "cause", { value: cause, configurable: true, writable: true });
  }
}
export function walletResponseError(value: unknown, traceId: string): WalletErrorDetails | null {
  if (!value || typeof value !== "object") return null;
  const event = value as Record<string, unknown>;
  if (event.type !== "transaction-signing-failed" || walletTraceId(event.trace_id) !== traceId) return null;
  const errorCode = walletProtocolErrorCode(event.error_code);
  const message = typeof event.error_message === "string" ? event.error_message.slice(0, 2048) : "";
  return { code: classifyWalletError(message, errorCode), errorCode, errorMessage: cleanWalletErrorMessage(message), errorSource: "wallet" };
}
